import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError, map } from "rxjs/operators";
import * as uuid from "uuid";

import { ConfigService } from "../config/config.service";
import { IBackendCallOptions } from "./backend-call-options.interface";
import { AmadeusGdsSessionCustomData } from "./naar-api-client";

export enum EBackendServiceRequestContentType {
    ApplicationJson = "application/json",
    ApplicationFormUrlEncoded = "application/x-www-form-urlencoded",
    MultipartFormData = "multipart/form-data",
    TextPlain = "text/plain",
    TextHtml = "text/html"
}

export class NrServerError extends Error {
	constructor(
		public message: string,
		public stacktrace: string,
		public code,
		public data: any
	) {
		super(message);
	}
}

@Injectable({
	providedIn: "root",
})
export class BackendService {
	constructor(private http: HttpClient, private config: ConfigService) {}

	public call<T = any>(urlPart: string, data: any = null): Observable<T> {
		const uniqueId = uuid.v4();
		const url: string =
			this.config.servicesEndpointRoot + urlPart + "?ts=" + uniqueId;
		data = data || {};
		data.uniqueID = uniqueId;

		return this.http
			.post<T>(url, data, {
				headers: new HttpHeaders({
					"Content-Type": "application/json",
				}),
				withCredentials: true,
			})
			.pipe(
				catchError((response) => {
					const error = response.error
						? response.error
						: { error: response.statusText };
					throw new NrServerError(
						error.error,
						error.stacktrace,
						error.code,
						error.values
					);
				}),
				map((response: any) => {
					if (response.result === "OK") {
						return response as T;
					} else {
						throw new NrServerError(
							response.error,
							response.stacktrace,
							response.code,
							response.values
						);
					}
				})
			);
	}

	public async post(
		urlPart: string,
		data: any = null,
		options: IBackendCallOptions = {}
	): Promise<any> {
		const uniqueId = uuid.v4();
		let url: string =
			(options && options.useAlternate
				? this.config.alternateServicesEndpointRoot
				: this.config.servicesEndpointRoot) +
			urlPart;

        url = url.includes("?") ? url + "&ts=" + uniqueId : url + "?ts=" + uniqueId;

		data = data || {};
		data.uniqueID = uniqueId;

		try {
			const headers = new HttpHeaders({
                "Content-Type": EBackendServiceRequestContentType.ApplicationJson,
			});
			if (options.hideProgressBar) {
				headers.append("ignoreProgressBar", "");
			}
			const response = await this.http
				.post<any>(url, data, {
					headers,
					withCredentials: true,
				})
				.toPromise();

			if (response.result === "OK") {
				return response;
			} else {
				throw new NrServerError(
					response.error,
					response.stacktrace,
					response.code,
					response.values
				);
			}
		} catch (response) {
			console.error(response);
			const error = response.error
				? response.error
				: { error: response.statusText };
			throw new NrServerError(
				error.error,
				error.stacktrace,
				error.code ?? response.status,
				error.values
			);
		}
	}

    public get<T = any>(
        url: string,
        contentType: EBackendServiceRequestContentType = EBackendServiceRequestContentType.ApplicationJson,
		withCredentials: boolean = true
    ): Observable<T> {
        const headers = new HttpHeaders({
            "Content-Type": contentType,
			"Accept": "*/*"
        });
        return this.http
            .get<T>(url, {
                headers: headers,
				withCredentials: withCredentials
            });
    }

	public async postFormData(
		urlPart: string,
		formData: FormData = null,
		options: IBackendCallOptions = {}
	): Promise<any> {
		const uniqueId = uuid.v4();
		const url: string =
			(options && options.useAlternate
				? this.config.alternateServicesEndpointRoot
				: this.config.servicesEndpointRoot) +
			urlPart +
			"?ts=" +
			uniqueId;

		formData.append(`uniqueID`, uniqueId);

		try {
			const headers = new HttpHeaders();

			if (options.hideProgressBar) {
				headers.append("ignoreProgressBar", "");
			}
			const response = await this.http
				.post<any>(url, formData, {
					headers,
					withCredentials: true,
				})
				.toPromise();

			if (response.result === "OK") {
				return response;
			} else {
				throw new NrServerError(
					response.error,
					response.stacktrace,
					response.code,
					response.values
				);
			}
		} catch (response) {
			console.error(response);
			const error = response.error
				? response.error
				: { error: response.statusText };
			throw new NrServerError(
				error.error,
				error.stacktrace,
				error.code,
				error.values
			);
		}
	}

	public callJupiter<T = any>(urlPart: string, data: any = null): Observable<T> {
		const uniqueId = uuid.v4();
		const url: string = this.config.servicesEndpointRoot + urlPart + "?ts=" + uniqueId;

		//? Prepare the request data making sure that all objects are converted to JSON
		function prepareRequest(input: any): unknown {
			if (input === null || typeof input !== 'object') {
				return input;
			}
			if (Array.isArray(input)) {
				return input.map(item => prepareRequest(item));
			}
			const output: any = {};
			for (const key of Object.keys(input)) {
				output[key] = prepareRequest(input[key]);
			}
			return output;
		}
		data = prepareRequest(data) || {};

		if (data.request?.sessionConnectorCustomData != null) {
			data.request.sessionConnectorCustomData["customDataConnectorCode"] = data.request.sessionConnectorCustomData["CustomDataConnectorCode"];
			delete data.request.sessionConnectorCustomData["CustomDataConnectorCode"];
			data.request.sessionConnectorCustomData = new AmadeusGdsSessionCustomData(data.request.sessionConnectorCustomData);
		}

		const content_ = JSON.stringify(data);

		return this.http
			.post<T>(url, content_, {
				headers: new HttpHeaders({
					"Content-Type": "application/json",
					"Accept": "application/json"
				}),
				withCredentials: true,
			})
			.pipe(
				catchError((response) => {
					const error = response.error
						? response.error
						: { error: response.statusText };
					throw new NrServerError(
						error,
						null,
						null,
						null
					);
				}),
				map((response: any) => {
					if (response.result === "OK") {
						return response as T;
					} else {
						throw new NrServerError(
							response.error,
							response.stacktrace,
							response.code,
							response.values
						);
					}
				})
			);
	}
}
